import { FormEvent, useEffect, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  Lieferbedingung,
  LieferbedingungInput,
  createLieferbedingung,
  deaktiviereLieferbedingung,
  fetchLieferbedingungen,
  updateLieferbedingung,
} from "@/lib/api"
import { SkeletonTabelle } from "@/components/Skeleton"
import { EmptyState } from "@/components/EmptyState"
import { useToast } from "@/components/Toast"
import { Button, FieldError, Input, TableWrapper, TD_CLASS, TH_CLASS, TR_CLASS } from "@/components/ui"
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet"

const LEER: LieferbedingungInput = { incoterm: "", name: "" }

/**
 * Pflegeseite Lieferbedingungen/Incoterms (Ninox `GF`) — AP-2.8. Die
 * Standort-Zuordnung selbst passiert im Organisation-Detail
 * (`LieferbedingungenZuordnungSection`), diese Seite pflegt den
 * Stammsatz der Lieferbedingung (Anlegen/Bearbeiten im Sheet).
 */
export function LieferbedingungenPage() {
  useEffect(() => {
    document.title = "Lieferbedingungen — RMNC Stammdaten"
  }, [])

  const queryClient = useQueryClient()
  const toast = useToast()
  const [form, setForm] = useState<LieferbedingungInput>(LEER)
  const [bearbeiteId, setBearbeiteId] = useState<string | null>(null)
  const [incotermFehler, setIncotermFehler] = useState<string | null>(null)
  const [zeigeSheet, setZeigeSheet] = useState(false)

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["lieferbedingungen", "alle"],
    queryFn: () => fetchLieferbedingungen(false),
  })

  const istEdit = bearbeiteId != null

  function oeffneNeu() {
    setBearbeiteId(null)
    setForm(LEER)
    setIncotermFehler(null)
    setZeigeSheet(true)
  }

  function oeffneBearbeiten(lb: Lieferbedingung) {
    setBearbeiteId(lb.id)
    setForm({
      incoterm: lb.incoterm,
      name: lb.name,
      transportart: lb.transportart,
    })
    setIncotermFehler(null)
    setZeigeSheet(true)
  }

  const anlegen = useMutation({
    mutationFn: (payload: LieferbedingungInput) => createLieferbedingung(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["lieferbedingungen"] })
      setForm(LEER)
      setZeigeSheet(false)
      toast.show("Lieferbedingung angelegt.", "success")
    },
    onError: (err: unknown) => {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.show(detail ?? "Lieferbedingung konnte nicht angelegt werden.", "error")
    },
  })

  const aktualisieren = useMutation({
    mutationFn: (payload: LieferbedingungInput) => updateLieferbedingung(bearbeiteId!, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["lieferbedingungen"] })
      setZeigeSheet(false)
      toast.show("Lieferbedingung aktualisiert.", "success")
    },
    onError: (err: unknown) => {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.show(detail ?? "Lieferbedingung konnte nicht aktualisiert werden.", "error")
    },
  })

  const speichern = istEdit ? aktualisieren : anlegen

  const deaktivieren = useMutation({
    mutationFn: (id: string) => deaktiviereLieferbedingung(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["lieferbedingungen"] })
      toast.show("Lieferbedingung deaktiviert.", "success")
    },
    onError: () => toast.show("Deaktivieren fehlgeschlagen.", "error"),
  })

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!form.incoterm.trim()) {
      setIncotermFehler("Incoterm ist erforderlich.")
      document.getElementById("lb-incoterm")?.focus()
      return
    }
    setIncotermFehler(null)
    speichern.mutate(form)
  }

  return (
    <div>
      <header className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Lieferbedingungen</h1>
          <p className="text-sm text-slate-600">
            Incoterms-Stammdaten (Ninox `GF`) — Standort-Zuordnung erfolgt im
            Organisations-Detail. AP-2.8, Liefer-API für Leitstand AP-5.5.
          </p>
        </div>
        <Button onClick={oeffneNeu}>+ Neue Lieferbedingung</Button>
      </header>

      <Sheet open={zeigeSheet} onOpenChange={setZeigeSheet}>
        <SheetContent sheetKey="lieferbedingung-anlegen" className="w-full overflow-y-auto sm:max-w-md">
          <SheetHeader>
            <SheetTitle>{istEdit ? "Lieferbedingung bearbeiten" : "Neue Lieferbedingung"}</SheetTitle>
            <SheetDescription>
              {istEdit ? "Incoterm-Stammsatz bearbeiten." : "Incoterm-Stammsatz anlegen."}
            </SheetDescription>
          </SheetHeader>
          <form id="lieferbedingung-neu-form" onSubmit={onSubmit} noValidate className="grid gap-4 py-4">
            <div className="space-y-1.5">
              <label htmlFor="lb-incoterm" className="block text-xs font-medium text-slate-700">
                Incoterm *
              </label>
              <Input
                id="lb-incoterm"
                required
                placeholder="z. B. EXW"
                value={form.incoterm}
                onChange={(e) => {
                  setForm({ ...form, incoterm: e.target.value.toUpperCase() })
                  if (incotermFehler) setIncotermFehler(null)
                }}
                invalid={!!incotermFehler}
                aria-describedby={incotermFehler ? "lb-incoterm-fehler" : undefined}
                className="uppercase"
              />
              <FieldError id="lb-incoterm-fehler">{incotermFehler}</FieldError>
            </div>
            <div className="space-y-1.5">
              <label className="block text-xs font-medium text-slate-700">Name</label>
              <Input
                value={form.name ?? ""}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <label className="block text-xs font-medium text-slate-700">Transportart</label>
              <Input
                value={form.transportart ?? ""}
                onChange={(e) => setForm({ ...form, transportart: e.target.value })}
              />
            </div>
          </form>
          <SheetFooter>
            <Button type="button" variant="secondary" onClick={() => setZeigeSheet(false)}>
              Abbrechen
            </Button>
            <Button type="submit" form="lieferbedingung-neu-form" disabled={speichern.isPending}>
              {speichern.isPending ? "Speichert…" : istEdit ? "Speichern" : "Anlegen"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {isError && (
        <div className="mb-4 flex items-center justify-between rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <span>Lieferbedingungen konnten nicht geladen werden.</span>
          <button
            type="button"
            onClick={() => refetch()}
            className="rounded-md font-medium underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
          >
            Erneut versuchen
          </button>
        </div>
      )}

      <TableWrapper>
        <table className="w-full text-sm">
          <thead>
            <tr>
              <th className={TH_CLASS}>Incoterm</th>
              <th className={TH_CLASS}>Name</th>
              <th className={TH_CLASS}>Transportart</th>
              <th className={TH_CLASS}>Status</th>
              <th className={TH_CLASS} />
            </tr>
          </thead>
          {isLoading ? (
            <SkeletonTabelle zeilen={5} spalten={5} />
          ) : (
            <tbody>
              {data?.map((lb) => (
                <tr
                  key={lb.id}
                  className={`${TR_CLASS} cursor-pointer`}
                  onClick={() => oeffneBearbeiten(lb)}
                >
                  <td className={`${TD_CLASS} font-mono text-slate-900`}>{lb.incoterm}</td>
                  <td className={TD_CLASS}>{lb.name ?? "—"}</td>
                  <td className={TD_CLASS}>{lb.transportart ?? "—"}</td>
                  <td className={TD_CLASS}>{lb.aktiv ? "Aktiv" : "Inaktiv"}</td>
                  <td className={`${TD_CLASS} text-right`}>
                    {lb.aktiv && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          deaktivieren.mutate(lb.id)
                        }}
                        disabled={deaktivieren.isPending}
                        className="rounded-md border border-red-300 px-2 py-1 text-xs text-red-700 hover:bg-red-50 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
                      >
                        Deaktivieren
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          )}
        </table>
      </TableWrapper>

      {!isLoading && !isError && (data?.length ?? 0) === 0 && (
        <EmptyState text="Noch keine Lieferbedingungen vorhanden." />
      )}
    </div>
  )
}
